import { Service, signal } from '@angular/core';
import { Observable, Subject } from 'rxjs';

export type CategoriaConsentimento = 'estatisticos' | 'preferencias' | 'usoDados';

export interface EstadoConsentimento {
  decidido: boolean;
  data: string | null;
  estatisticos: boolean;
  preferencias: boolean;
  usoDados: boolean;
}

const CHAVE = 'consentimento_lgpd_carta_servico';
const VERSAO = 1;

const ESTADO_INICIAL: EstadoConsentimento = {
  decidido: false,
  data: null,
  estatisticos: false,
  preferencias: false,
  usoDados: false,
};

@Service()
export class ConsentService {
  private readonly estadoAtual = signal<EstadoConsentimento>(this.carregar());
  private readonly abrirSubject = new Subject<void>();

  readonly estado = this.estadoAtual.asReadonly();
  readonly abrirPreferencias$: Observable<void> = this.abrirSubject.asObservable();

  foiDecidido(): boolean {
    return this.estadoAtual().decidido;
  }

  permitido(categoria: CategoriaConsentimento): boolean {
    return this.estadoAtual()[categoria] === true;
  }

  salvar(parcial: Partial<Record<CategoriaConsentimento, boolean>>): void {
    const atual = this.estadoAtual();
    const novo: EstadoConsentimento = {
      decidido: true,
      data: this.agoraISO(),
      estatisticos: parcial.estatisticos ?? atual.estatisticos,
      preferencias: parcial.preferencias ?? atual.preferencias,
      usoDados: parcial.usoDados ?? atual.usoDados,
    };
    this.persistir(novo);
    this.estadoAtual.set(novo);
  }

  aceitarTudo(): void {
    this.salvar({ estatisticos: true, preferencias: true, usoDados: true });
  }

  recusarTudo(): void {
    this.salvar({ estatisticos: false, preferencias: false, usoDados: false });
  }

  revogar(): void {
    try {
      localStorage.removeItem(CHAVE);
    } catch {
    }
    this.estadoAtual.set({ ...ESTADO_INICIAL });
  }

  abrirConfiguracoes(): void {
    this.abrirSubject.next();
  }

  private carregar(): EstadoConsentimento {
    try {
      const bruto = localStorage.getItem(CHAVE);
      if (!bruto) {
        return { ...ESTADO_INICIAL };
      }
      const dados = JSON.parse(bruto);
      if (!dados || dados.versao !== VERSAO) {
        return { ...ESTADO_INICIAL };
      }
      return {
        decidido: true,
        data: typeof dados.data === 'string' ? dados.data : null,
        estatisticos: dados.estatisticos === true,
        preferencias: dados.preferencias === true,
        usoDados: dados.usoDados === true,
      };
    } catch {
      return { ...ESTADO_INICIAL };
    }
  }

  private persistir(estado: EstadoConsentimento): void {
    try {
      localStorage.setItem(
        CHAVE,
        JSON.stringify({
          versao: VERSAO,
          data: estado.data,
          estatisticos: estado.estatisticos,
          preferencias: estado.preferencias,
          usoDados: estado.usoDados,
        })
      );
    } catch {
    }
  }

  private agoraISO(): string {
    return new Date().toISOString();
  }
}
